<template>
  <kbd :class="rootClass" :title="title">{{ label }}</kbd>
</template>

<script lang="ts">
export const KeyHintSize = {
  Sm: 'sm',
  Md: 'md',
} as const

export type KeyHintSize = (typeof KeyHintSize)[keyof typeof KeyHintSize]

export const KeyHintVariant = {
  Default: 'default',
  Accent: 'accent',
  Muted: 'muted',
} as const

export type KeyHintVariant = (typeof KeyHintVariant)[keyof typeof KeyHintVariant]
</script>

<script setup lang="ts">
import { computed } from 'vue'

export interface KeyHintProps {
  /** The label rendered inside the key cap (e.g. "Q", "TAB", "ESC"). */
  readonly label: string
  readonly size?: KeyHintSize
  readonly variant?: KeyHintVariant
  /** Optional tooltip / aria-label describing what the key does. */
  readonly title?: string
}

const props = withDefaults(defineProps<KeyHintProps>(), {
  size: KeyHintSize.Sm,
  variant: KeyHintVariant.Default,
})

const baseClass =
  'inline-flex items-center justify-center font-mono font-semibold leading-none rounded-sm border border-border-strong border-b-2 select-none whitespace-nowrap tracking-[0.04em] uppercase'

const sizeClass = computed<string>(() =>
  props.size === KeyHintSize.Md ? 'min-w-7 px-2 py-1 text-sm' : 'min-w-5 px-2 py-0.5 text-xs',
)

const variantClass = computed<string>(() => {
  switch (props.variant) {
    case KeyHintVariant.Accent:
      return 'bg-accent/[0.16] text-accent-hover border-accent/50'
    case KeyHintVariant.Muted:
      return 'bg-bg text-text-muted border-border'
    case KeyHintVariant.Default:
    default:
      return 'bg-bg-elevated text-text-secondary border-border-strong'
  }
})

// Inline string concatenation kept (no `ReadonlyArray` here) — the three
// fragments are all guaranteed strings by the `computed` return types and
// `.trim()` is a no-op, so the array refactor would only add ceremony.
const rootClass = computed<string>(() => `${baseClass} ${sizeClass.value} ${variantClass.value}`)
</script>